/**
 * @fileoverview The banner zone at the top of the Control Panel chrome.
 *
 * The chrome, in the order the capture has it:
 *
 *   div.workspace-banner-components__wrapper   [data-test-id="banners-wrapper"]
 *     span.bp6-popover-target
 *       div.workspace-banner-components__banner
 *          .workspace-banner-components__classification-banner
 *          .workspace-banner-components__top
 *          .workspace-banner-components__show-when-printing
 *         span > p                             the classification text
 *     div.control-panel-shell__header          the top bar
 *     div.control-panel-shell__settings        sidebar + content
 *
 * OUR BANNER WAS SOMEWHERE ELSE ENTIRELY. `ClassificationBanner` renders in the
 * AppShell, outside this subtree, so on this route the wrapper's first child
 * was the top bar. That is not only a missing element — it is where the DOM
 * comparison diverged and stopped, at 43 nodes of 356.
 *
 * THE SPAN IS NOT DECORATION. Upstream the banner is a Blueprint popover target
 * (it explains the marking on hover), so the wrapper's first child is a `span`,
 * not the banner div. Skipping it would shift every descendant by one and break
 * the walk again one level lower.
 *
 * CLASSES COME FROM THE PARTS MAP, not from literals. I wrote this file twice
 * wrongly first: once with unhashed names, which normalise clean in the DOM
 * diff and pick up no rules at all, and once with `__10dv3w9` typed straight
 * into the JSX — a hash nothing regenerates, nothing validates, and no reader
 * can trace to a module. The namespace was simply absent from the surface's
 * allowlist in sync-foundry-surface.mjs; adding it there is the fix, and this
 * file just indexes the result.
 *
 * The colours are inline upstream — `color: rgb(255,255,255)` on
 * `background: rgb(0,122,51)` — because a classification banner's colour is
 * data, not theme: it comes from the marking, and a stylesheet that hard-codes
 * green is one that shows the wrong colour for a different marking.
 */

import type { ReactNode } from "react";

import { workspaceBannerComponents as wbc } from "../../../../components/control-panel/foundry-parts.generated";

export interface WorkspaceBannersProps {
  /** The classification line, e.g. `UNCLASSIFIED // NOTIONAL DATA`. */
  readonly text: string;
  /** Marking colours. Defaults are the unclassified green the capture uses. */
  readonly color?: string;
  readonly background?: string;
  /** The top bar and the settings container. */
  readonly children: ReactNode;
}

export function WorkspaceBanners({
  text,
  color = "rgb(255, 255, 255)",
  background = "rgb(0, 122, 51)",
  children,
}: WorkspaceBannersProps): React.ReactElement {
  return (
    <div className={wbc["wrapper"]} data-test-id="banners-wrapper">
      <span className="bp6-popover-target">
        <div
          className={[
            wbc["banner"],
            wbc["classification-banner"],
            wbc["top"],
            // Printed pages carry the marking too — losing it on paper is the
            // one place a classification banner most needs to survive.
            wbc["show-when-printing"],
          ].join(" ")}
          style={{ color, background }}
          id="workspace-static-banner-top"
          data-test-id="workspace-static-banner-top"
        >
          <span>
            <p>{text}</p>
          </span>
        </div>
      </span>
      {children}
    </div>
  );
}
